import { Calendar } from '@fullcalendar/core';

/**
 * Class placed on `<body>` for the duration of a calendar print. `_print.scss`
 * keys every print rule off it, so nothing in the print stylesheet applies to an
 * ordinary Ctrl+P of some other page.
 */
export const PRINT_BODY_CLASS = 'print-calendar';

/** Wrapper each host puts around its calendar; `_print.scss` reveals one of these. */
export const PRINT_AREA_CLASS = 'calendar-print-area';

/**
 * Marks the one print area being printed right now.
 *
 * More than one calendar can be on the page at once: every floating dialog
 * showing resource details carries its own, and the Resources Directory adds
 * another when it is in calendar view. Revealing them all would stack several
 * absolutely-positioned grids on the same corner of the page, so the reveal is
 * keyed to this class rather than to {@link PRINT_AREA_CLASS} alone.
 */
export const PRINTING_CLASS = 'is-printing';

/** Schedules the work that must happen after the calendar has re-rendered. */
export type PrintScheduler = (callback: () => void) => void;

/**
 * Builds the name the browser offers when the user chooses "Save as PDF".
 *
 * Browsers derive that filename from `document.title`, so this is the only lever
 * over it. The period comes from the calendar's own heading, which is the string
 * printed at the top of the page, so the file is named for the month the reader
 * is looking at rather than the day it happened to be printed. In the month view
 * that gives "<label> - June 2026"; other views contribute their own range.
 */
export function printDocumentTitle(label: string, period: string): string {
  return `${label} - ${period}`;
}

export interface PrintCalendarOptions {
  /**
   * The calendar to print. Typed as `Calendar` rather than `CalendarApi`
   * because this needs the root element, which only the concrete class exposes.
   * Both callers already hold one: `FullCalendarComponent.getApi()` returns it.
   */
  api: Calendar;
  /**
   * The `dayMaxEvents` value to put back afterwards. Printing forces this to
   * `false` so no day is collapsed behind a "+N more" link, because a printout
   * that silently hides entries is worse than no printout.
   */
  dayMaxEvents: number | boolean;
  /**
   * Leading part of the "Save as PDF" filename, such as
   * "Resources Directory - Time Off". The calendar's current period heading is
   * appended to it, so the caller does not have to track which month is on
   * screen. Omit to leave the page title alone.
   */
  documentTitlePrefix?: string;
  /**
   * Runs the print once the calendar has had a chance to re-render with the
   * collapse lifted. Defaults to the next animation frame; tests pass a
   * synchronous scheduler.
   */
  schedule?: PrintScheduler;
}

const defaultScheduler: PrintScheduler = (callback) =>
  requestAnimationFrame(callback);

/**
 * True between the start of a print and its restore.
 *
 * Module-level rather than per-call because the print dialog is global: two
 * calendars cannot usefully print at once. Without it, a second call entering
 * before the first restores captures the already-swapped `document.title` as
 * its own "previous" value and puts that back at the end, stranding the browser
 * tab on the print filename for the rest of the session. The chip binds
 * `keydown.enter`, so holding Enter is enough to reach it.
 */
let printInProgress = false;

/**
 * Prints a FullCalendar view.
 *
 * Marks the document, expands any collapsed day cells, opens the browser print
 * dialog, and then puts both back exactly as they were.
 *
 * The restore is registered twice on purpose: browsers disagree about whether
 * `afterprint` fires before or after `window.print()` returns, and Safari has
 * historically not fired it at all for a cancelled dialog. An internal guard
 * makes the restore idempotent, so whichever path arrives first wins and the
 * other becomes a no-op. Leaving the body class behind would silently break the
 * next print of any page, so this is the failure worth spending a guard on.
 */
export function printCalendar(options: PrintCalendarOptions): void {
  if (printInProgress) return;
  printInProgress = true;

  const {
    api,
    dayMaxEvents,
    documentTitlePrefix,
    schedule = defaultScheduler,
  } = options;

  // Read the heading before anything changes, so the filename names the period
  // actually on screen.
  const documentTitle = documentTitlePrefix
    ? printDocumentTitle(documentTitlePrefix, api.view.title)
    : undefined;
  const previousTitle = document.title;
  // The wrapper around this particular calendar, which may be one of several on
  // the page. Optional chaining rather than a hard requirement so a host that
  // has not wrapped its calendar still prints something rather than throwing.
  const printArea = api.el?.closest(`.${PRINT_AREA_CLASS}`) ?? null;
  let restored = false;
  const restore = (): void => {
    if (restored) return;
    restored = true;
    printInProgress = false;
    window.removeEventListener('afterprint', restore);
    document.body.classList.remove(PRINT_BODY_CLASS);
    printArea?.classList.remove(PRINTING_CLASS);
    if (documentTitle) document.title = previousTitle;
    api.setOption('dayMaxEvents', dayMaxEvents);
  };

  document.body.classList.add(PRINT_BODY_CLASS);
  printArea?.classList.add(PRINTING_CLASS);
  if (documentTitle) document.title = documentTitle;
  api.setOption('dayMaxEvents', false);
  window.addEventListener('afterprint', restore);

  schedule(() => {
    try {
      window.print();
    } finally {
      restore();
    }
  });
}
